<i18n locale="en">
{
  "actions": {
    "replace": "Replace",
    "remove": "Remove",
    "load": "Upload an image",
    "select": "Select Image",
    "source": "Edit Image Source"
  },
  "context": {
    "editing": "Editing {label}"
  },
  "labels": {
    "preview": "Image Preview",
    "source": "Image source: {source}"
  }
}
</i18n>
<template>
  <div class="own-image">
    <OwnFile
      :model-value="localImage"
      accept="image/png, image/jpeg"
      :allow-delete="allowDelete"
      :disabled="disabled"
      :edit-button-replace-label="t('actions.replace')"
      :edit-button-select-label="t('actions.select')"
      :error="error"
      :hide-edit-button="hideEditButton"
      @update:model-value="onFileChange"
    >
      <template #empty>
        <PhCamera class="misc-brand" size="24" />
        <OwnType
          color="primary"
          align="center"
          :text="uploadLabel ?? t('actions.load')"
          variant="button-regular"
        />
      </template>
      <template #preview="{ previewValue }">
        <img
          class="own-image__preview"
          :src="
            generatePreviewUrl ? generatePreviewUrl(previewValue) : previewValue
          "
          :alt="t('labels.preview')"
        />
      </template>
    </OwnFile>
    <div
      v-if="
        isSuperAdmin && showImageSourceSelector && hasImageSourceSelectorFeature
      "
      class="pt-2"
    >
      <OwnButton
        class="mb-2 w-full"
        color="primary"
        :text="t('actions.source')"
        @click="openSourceDialog(imageSource)"
      />
      <OwnTag
        color="primary"
        :text="t('labels.source', { source: displayImageSource })"
      />
    </div>

    <ImageCropper
      v-if="showImageEditor"
      :file-type="fileType"
      :image="localImage"
      :aspect-ratio="aspectRatio"
      :crop-height="cropHeight"
      :crop-width="cropWidth"
      :show-image-source-selector="showImageSourceSelector"
      :title="t('context.editing', { label })"
      @close="onCancel"
      @update="updateImage"
    />

    <ImageSourceDialog
      v-model="showSourceDialog"
      v-model:selected-source="selectedSource"
      :processing="loadingMetadata"
      @confirm="confirmSourceUpdate"
      @cancel="cancelSourceUpdate"
    />
  </div>
</template>
<script lang="ts">
import { PhCamera } from '@phosphor-icons/vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useAuthStore } from '@/auth/useAuthStore'
import { logError } from '@/logger'
import notify from '@/mixins/notify'
import { OlympusClient } from '@/OlympusClient'
import { OwnButton } from '@/ui/OwnButton'
import { OwnFile } from '@/ui/OwnFile'
import { OwnTag } from '@/ui/OwnTag'
import { OwnType } from '@/ui/OwnType'
import { toDataUrl } from '@/utils/toDataUrl'
import { useReactiveFeatureFlag } from '@/utils/useFeatureFlags'

import ImageCropper from './ImageCropper.vue'
import ImageSourceDialog from './ImageSourceDialog.vue'
import { useImageSourceDialog } from './useImageSourceDialog'

export default {
  name: 'ImageInput',
  components: {
    ImageCropper,
    ImageSourceDialog,
    OwnButton,
    OwnFile,
    OwnTag,
    OwnType,
    PhCamera,
  },
  mixins: [notify],
  inheritAttrs: false,
  emits: ['update:modelValue'],
  setup() {
    const { t } = useI18n()
    const { isSuperAdmin } = storeToRefs(useAuthStore())
    const { enabled: hasImageSourceSelectorFeature } = useReactiveFeatureFlag(
      'hermes.image-source-selector'
    )
    const {
      selectedSource,
      showSourceDialog,
      openSourceDialog,
      closeSourceDialog,
      resetSource,
    } = useImageSourceDialog()

    return {
      closeSourceDialog,
      hasImageSourceSelectorFeature,
      isSuperAdmin,
      openSourceDialog,
      resetSource,
      selectedSource,
      showSourceDialog,
      t,
    }
  },
  props: {
    /** Allow removing the image (setting to null) */
    allowDelete: { type: Boolean, default: false },

    /** Aspect Ratio */
    aspectRatio: { type: Number, default: undefined },

    /** Force Crop Height */
    cropHeight: { type: Number, default: undefined },

    /** Force Crop Width */
    cropWidth: { type: Number, default: undefined },

    /** When `true`, the input will be disabled */
    disabled: { type: Boolean, default: false },

    /** Has error */
    error: { type: Boolean, default: false },

    /**
     * File type to upload. Use `'jpeg'` (the default) for photos and `'png'` for illustrations (e.g. the logo).
     * @default 'jpeg'
     */
    fileType: {
      type: String,
      default: 'jpeg',
      validator: (v: string) => ['jpeg', 'png'].includes(v),
    },

    /** Generate preview url */
    generatePreviewUrl: { type: Function, default: undefined },

    /** Full height */
    hFull: { type: Boolean, default: false },

    /** Hides the edit button, clicking the image still works */
    hideEditButton: { type: Boolean, default: false },

    /** Label of image */
    label: { type: String, default: undefined },

    /** @model Image (url or base64 enc string) */
    modelValue: { type: String, default: undefined },

    /** Show image source selector */
    showImageSourceSelector: { type: Boolean, default: false },

    /** Label for upload button */
    uploadLabel: { type: String, default: undefined },
  },
  data() {
    return {
      imageSource: null as string | null,
      loadingMetadata: false,
      localImage: '',
      showImageEditor: false,
    }
  },
  computed: {
    displayImageSource() {
      if (!this.imageSource) return this.$t('image-sources.unknown')

      // Use i18n for source labels, fallback to formatted source if not found
      const sourceKey = `image-sources.${this.imageSource}`
      return this.$t(sourceKey) || this.formatSourceFallback(this.imageSource)
    },
    modelImage: {
      get() {
        return this.modelValue
      },
      set(value: string) {
        this.$emit('update:modelValue', value)
      },
    },
  },
  watch: {
    modelImage(newImage, oldImage) {
      if (newImage !== oldImage) {
        this.localImage = newImage
        this.fetchImageMetadata(newImage)
      }
    },
  },
  created() {
    const { modelImage } = this

    this.localImage = modelImage
  },
  methods: {
    cancelSourceUpdate() {
      this.closeSourceDialog()
    },
    async confirmSourceUpdate() {
      if (!this.modelValue) {
        this.closeSourceDialog()
        return
      }

      try {
        this.loadingMetadata = true

        // Update the image metadata using the bulk update endpoint
        await OlympusClient.images.v1.bulkUpdateMetadata({
          body: {
            updates: [
              {
                imageUrl: this.modelValue,
                metadata: {
                  source: this.selectedSource,
                },
              },
            ],
          },
        })

        // Update local imageSource to reflect the change
        this.imageSource = this.selectedSource
        this.closeSourceDialog()
        this.$notify('Image source updated successfully', 'success')
      } catch (error) {
        logError(error)
        this.$notify('Failed to update image source', 'error')
      } finally {
        this.loadingMetadata = false
      }
    },
    async fetchImageMetadata(imageUrl: string | null) {
      this.imageSource = null

      if (!imageUrl || this.loadingMetadata) {
        return
      }

      try {
        this.loadingMetadata = true

        const response = await OlympusClient.images.v1.image.getImage({
          query: { imageUrl },
        })

        this.imageSource = response.image.metadata?.source || null
      } catch (error) {
        logError(error)
      } finally {
        this.loadingMetadata = false
      }
    },
    formatSourceFallback(source: string) {
      // Convert to sentence case as fallback
      return source
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (char: string) => char.toUpperCase())
    },
    onCancel() {
      this.showImageEditor = false
      this.localImage = this.modelImage
    },
    async onFileChange(file: File) {
      if (file) {
        this.localImage = await toDataUrl(file)
        this.showImageEditor = true
      } else {
        this.updateImage(null)
      }
    },
    updateImage(croppedImage: string) {
      this.localImage = ''
      this.modelImage = croppedImage
      this.showImageEditor = false
    },
  },
}
</script>
<style lang="scss" scoped>
.own-image {
  &__preview {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%; // Leave space for source text
    object-fit: contain;
    border-radius: 8px;
    flex: 1;
  }
}
</style>
